import {
  ClipboardList,
  Factory,
  PackageCheck,
  CreditCard,
  Users,
  Building2,
  Settings,
  History,
  Palette,
  LayoutDashboard,
  Package,
  Target,
  Paintbrush,
  BarChart3,
  MailCheck,
  Scale,
  GraduationCap,
  Calculator,
  Receipt,
  Shirt,
  Boxes,
  Truck,
  Warehouse,
  ClipboardCheck,
} from "lucide-react";
import { ShirtPrint } from "@/components/icons/ShirtPrint";
import { useCallback } from "react";
import { usePermissions } from "@/contexts/PermissionsContext";
import { featureGateOpen, type FeatureGate } from "@/lib/platformConstants/featureSwitches";
import { useInventoryEnabled } from "@/lib/platformConstants/usePlatformConstantsQuery";

/**
 * Dashboard Module Registry
 *
 * Single source of truth for which tabs are available per role.
 * Each module defines its sidebar entry (key, label, icon, group) and
 * which roles can access it.
 *
 * Sidebar grouping:
 *   - `primary`   → top section, the day-to-day workflows users live in
 *   - `secondary` → "More" section below a divider; still always visible,
 *                   just visually de-emphasised so the primary five stay
 *                   the navigational anchor
 *
 * Order within each group is the order entries appear in the sidebar.
 * The primary order is intentionally the design→licensing→quote→order
 * lifecycle, top to bottom:
 *   Sales Pipeline → Schools → Accounts → Users → Quoter →
 *   Designs → Licensing → Orders
 * Sales Pipeline stays pinned first (operational priority), then the
 * artefacts of a deal follow the order they're produced in: a design is
 * requested, its products are licensed, the deal is quoted, then the order
 * is placed. Licensing sits between Design Requests and Quote Management
 * because a product must clear licensing before it can be quoted.
 */

export type DashboardTab =
  | "requests"
  | "design-work"
  | "designer-overview"
  | "licensing"
  | "payments"
  | "production"
  | "suppliers"
  | "supplier-performance"
  | "printer-orders"
  | "blanks-orders"
  | "fc-orders"
  | "warehouse-orders"
  | "inventory"
  | "printer-performance"
  | "order-list"
  | "order-overview"
  | "sales-pipeline"
  | "quoter"
  | "quote-requests"
  | "users"
  | "school-management"
  | "accounts"
  | "product-catalog"
  | "configuration"
  | "email-config"
  | "activity"
  | "theme-editor"
  | "usage-tracker";

export type DashboardModuleGroup = "primary" | "secondary";

export interface DashboardModule {
  key: DashboardTab;
  path: string;
  label: string;
  icon: React.ElementType;
  archived?: boolean;
  /**
   * Kept out of the sidebar and the mobile bar, but NOT out of this registry.
   * The registry is also how a URL resolves to a tab and to its `module.*`
   * capability (`getModuleByPath`), so deleting the row would send the path to
   * the fallback tab and render the wrong page. Hiding is a nav decision; the
   * route stays reachable by URL and from whatever still links to it.
   */
  hiddenFromNav?: boolean;
  /**
   * Other path prefixes this module owns besides `path`. Checked after exact
   * and `path` prefix matches, so a more specific module (e.g.
   * `/suppliers/performance`) still wins.
   */
  aliasPrefixes?: string[];
  /** Sidebar grouping — drives the "More" divider. Defaults are explicit
   *  per module rather than computed to keep the registry self-documenting. */
  group: DashboardModuleGroup;
  /**
   * Tailwind class for the icon color when shown in the sidebar.
   * Each module gets a distinct hue so the sidebar reads as a colorful column
   * of pills rather than a monochrome list.
   */
  iconClass: string;
  /**
   * A feature switch the entry waits on (`inventory`). While closed the entry
   * is out of the nav and its route redirects to /production.
   */
  featureGate?: FeatureGate;
}

/**
 * NOTE: per-module access is no longer gated by a `roles[]` array here. The
 * gate now lives in the DB (role_capabilities, key `module.<key>`) and is read
 * through `userHasPermission(...)` — see useAccessibleModules() below. This
 * registry is now purely nav metadata (key/path/label/icon/group).
 */
export const DASHBOARD_MODULES: DashboardModule[] = [
  /** ── Primary section (pinned, in user-requested priority order) ──────── */
  { key: "sales-pipeline",    path: "/sales-pipeline",        label: "Sales Pipeline",        icon: Target,        iconClass: "text-icon-discount", group: "primary" },
  { key: "school-management", path: "/school-management",     label: "Schools",               icon: GraduationCap, iconClass: "text-icon-org",      group: "primary" },
  { key: "accounts",          path: "/accounts",              label: "Accounts",              icon: Building2,     iconClass: "text-icon-sales", group: "primary" },
  { key: "users",             path: "/user-management",       label: "Users",                 icon: Users,         iconClass: "text-icon-admin",    group: "primary" },
  /** Hidden from nav — still reachable by URL and from the Sales Pipeline deal dialog. */
  { key: "quoter",            path: "/quote-management",      label: "Quoter",                icon: Calculator,    iconClass: "text-icon-cash",     group: "primary", hiddenFromNav: true },
  /**
   * Distinct from Quoter above: that is the legacy `quotes` table, this is the
   * queue of quote requests raised against design versions. Same word, two
   * different domains — hence two modules and two capabilities.
   */
  { key: "quote-requests",    path: "/quote-requests",        label: "Quote Requests",        icon: Receipt,       iconClass: "text-icon-sales",    group: "primary" },
  { key: "product-catalog",   path: "/product-catalog",       label: "Product Catalog",       icon: Shirt,         iconClass: "text-icon-products", group: "primary" },
  { key: "requests",          path: "/design-requests",       label: "Designs",               icon: ClipboardList, iconClass: "text-icon-tasks",    group: "primary" },
  { key: "design-work",       path: "/design-work",           label: "Design Work",           icon: Palette,           iconClass: "text-icon-designer", group: "primary" },
  { key: "designer-overview", path: "/designer-overview",     label: "Designer Overview",     icon: LayoutDashboard,   iconClass: "text-icon-designer", group: "primary" },
  { key: "licensing",         path: "/licensing",             label: "Licensing",  icon: Scale,         iconClass: "text-icon-tools",    group: "primary" },
  { key: "order-list",        path: "/orders",                label: "Orders",                icon: Package,       iconClass: "text-icon-products", group: "primary" },
  { key: "payments",          path: "/payments",              label: "Payments",              icon: CreditCard,    iconClass: "text-icon-cash",     group: "primary" },
  { key: "production",        path: "/production",            label: "Production",            icon: Factory,       iconClass: "text-icon-products", group: "primary" },
  /** Key stays `suppliers`: it is the `module.suppliers` capability in the DB. */
  /**
   * `/suppliers/:id/board` and `/suppliers/:id/lanes` — one supplier's board as
   * they see it, and Manage statuses — are part of Supplier Orders and share
   * its `module.suppliers` gate.
   */
  { key: "suppliers",         path: "/supplier-orders",       label: "Supplier Orders",       icon: PackageCheck,  iconClass: "text-icon-products", group: "primary", aliasPrefixes: ["/suppliers/"] },
  /**
   * Supplier performance. Reached from the Suppliers board — its toolbar, its
   * by-supplier rows, and the move dialog — rather than from the sidebar.
   *
   * `hiddenFromNav` because access is gated on `module.supplier-performance`,
   * and that capability row does not exist in the DB yet; a nav entry would
   * simply never render. Drop the flag once the capability is seeded. The
   * entry is registered regardless so `getModuleByPath` resolves the route and
   * the page inherits the dashboard shell.
   */
  { key: "supplier-performance", path: "/suppliers/performance", label: "Supplier Performance", icon: PackageCheck, iconClass: "text-icon-products", group: "primary", hiddenFromNav: true },
  /** Printer, FC and warehouse flow (frontend spec 2.4). ShirtPrint: printers print onto garments, not paper (owner 2026-10-27). */
  { key: "printer-orders",    path: "/printer-orders",        label: "Printer Orders",        icon: ShirtPrint,    iconClass: "text-icon-products", group: "primary" },
  { key: "blanks-orders",     path: "/blanks-orders",         label: "Blanks Orders",         icon: Boxes,         iconClass: "text-icon-products", group: "primary" },
  { key: "fc-orders",         path: "/fc-orders",             label: "FC Orders",             icon: Truck,         iconClass: "text-icon-products", group: "primary", aliasPrefixes: ["/fulfillment-centers/"] },
  { key: "warehouse-orders",  path: "/warehouse-orders",      label: "Warehouse Orders",      icon: Warehouse,     iconClass: "text-icon-products", group: "primary", aliasPrefixes: ["/warehouses/"] },
  { key: "inventory",         path: "/inventory",             label: "Inventory",             icon: ClipboardCheck, iconClass: "text-icon-products", group: "primary", featureGate: "inventory" },
  /** Reached from the Printer Orders board "Performance" link, like Supplier Performance. */
  { key: "printer-performance", path: "/printers",            label: "Printer Performance",   icon: ShirtPrint,    iconClass: "text-icon-products", group: "primary", hiddenFromNav: true },

  /** ── Secondary section ("More") — operations + admin/config ──────────── */
  { key: "order-overview",    path: "/order-overview",        label: "Order Overview",        icon: BarChart3,     iconClass: "text-icon-sales",    group: "secondary" },
  { key: "configuration",     path: "/product-configuration", label: "Product Configuration", icon: Settings,      iconClass: "text-icon-settings", group: "secondary" },
  { key: "email-config",      path: "/email-configuration",   label: "Email Configuration",   icon: MailCheck,     iconClass: "text-icon-cash",     group: "secondary" },
  { key: "activity",          path: "/past-activity",         label: "Past Activity",         icon: History,       iconClass: "text-icon-notes",    group: "secondary" },
  { key: "theme-editor",      path: "/theme-editor",          label: "Theme Editor",          icon: Paintbrush,    iconClass: "text-icon-designs",  group: "secondary" },
  { key: "usage-tracker",     path: "/usage-tracker",         label: "Usage Tracker",         icon: BarChart3,     iconClass: "text-icon-help",     group: "secondary" },
];

/**
 * Returns the dashboard modules the current user may access, gated by the
 * `module.<key>` capability (read from the DB via the permissions helper).
 * Replaces the old role-array filter — the single source of truth for module
 * access now lives in role_capabilities, not a hardcoded list here.
 */
export function useAccessibleModules(): DashboardModule[] {
  const { userHasPermission } = usePermissions();
  const isOpen = useFeatureGateOpen();
  return DASHBOARD_MODULES.filter((m) => isOpen(m.featureGate) && userHasPermission(`module.${m.key}`));
}

/** Whether a module's feature switch is open (true for ungated modules). */
export function useFeatureGateOpen(): (gate: FeatureGate | undefined) => boolean {
  const inventory = useInventoryEnabled();
  return useCallback(
    (gate: FeatureGate | undefined) => featureGateOpen(gate, { inventory }),
    [inventory],
  );
}

/**
 * Returns the module matching a given URL path, or undefined.
 */
export function getModuleByPath(path: string): DashboardModule | undefined {
  /**
   * Exact match first, then prefix match so sub-routes like
   * /school-management/:schoolId still resolve to the parent module
   * (and inherit its sidebar highlight + role gate).
   */
  return (
    DASHBOARD_MODULES.find((m) => m.path === path) ??
    DASHBOARD_MODULES.find((m) => path.startsWith(m.path + "/")) ??
    DASHBOARD_MODULES.find((m) => m.aliasPrefixes?.some((prefix) => path.startsWith(prefix)))
  );
}

/**
 * Returns the portal title for a given role.
 */
export function getPortalTitle(role: string): string {
  switch (role) {
    case "admin": return "Admin Portal";
    case "ops": return "OPS Portal";
    case "dsa": return "DSA Portal";
    case "sales": return "Sales Portal";
    case "licensing_ops": return "Licensing OPS Portal";
    default: return "Intern Portal";
  }
}
